import * as monaco from 'monaco-editor'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ErrorBlock } from '~/components/common/ErrorBlock'
import { cm } from '~/utils/utils'
import { getMonacoOptions, initializeMonaco } from './monacoInitializer'
import { ValidationResult, validationStrategies } from './validationStrategies'

export type Language =
  | 'xml'
  | 'json'
  | 'csv'
  | 'yaml'
  | 'edi'
  | 'edifact'
  | 'hl7'
  | 'auto'
  | 'plaintext'

interface MonacoTextAreaProps {
  value: string
  onChange: (value: string) => void
  language?: Language
  theme?: string
  options?: monaco.editor.IEditorOptions
  placeholder?: string
  className?: string
  innerClassName?: string
  requireLangs?: Language[]
  onError: () => void
  onErrorClear: () => void
  disabled?: boolean
  backgroundColor?: string
  disableFocusEffect?: boolean
  noBorder?: boolean
}

await initializeMonaco('#f9f9f9')

export const MonacoTextEditor = ({
  value,
  onChange,
  language = 'auto',
  theme = 'vs-dark',
  options = {},
  placeholder = '',
  className,
  innerClassName,
  onError,
  onErrorClear,
  disabled = false,
  backgroundColor = '#ffffff',
  disableFocusEffect = false,
  noBorder = false,
  requireLangs = []
}: MonacoTextAreaProps) => {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const errorDecorations = useRef<string[]>([])
  const [validationError, setValidationError] = useState<string | null>(null)
  const [detectedLanguage, setDetectedLanguage] = useState<string>(language)
  const languageRef = useRef(language)
  const requireLangsRef = useRef(requireLangs)
  const onChangeRef = useRef(onChange)
  const onErrorRef = useRef(onError)
  const onErrorClearRef = useRef(onErrorClear)
  const validateContentRef = useRef<((content: string, language: string) => void) | null>(null)
  const [hasContent, setHasContent] = useState(!!value.trim())

  useEffect(() => {
    requireLangsRef.current = requireLangs
    onChangeRef.current = onChange
    onErrorRef.current = onError
    onErrorClearRef.current = onErrorClear
  }, [requireLangs, onChange, onError, onErrorClear])

  const markErrors = useCallback((result: ValidationResult) => {
    if (!editorRef.current) return

    const model = editorRef.current.getModel()
    if (!model) return

    const line = result.line || 1
    const column = result.column || 1
    const endColumn = model.getLineMaxColumn(line)

    const newDecorations = [
      {
        range: new monaco.Range(line, column, line, endColumn),
        options: {
          className: 'squiggly-error',
          hoverMessage: { value: result.error || 'Validation Error' },
          inlineClassName: 'error-underline'
        }
      }
    ]

    errorDecorations.current = editorRef.current.deltaDecorations(
      errorDecorations.current,
      newDecorations
    )
  }, [])

  const detectLanguage = useCallback((content: string) => {
    // If requireLangs is provided, only check those languages
    const languagesToCheck =
      requireLangsRef.current.length > 0
        ? requireLangsRef.current
        : (['xml', 'json', 'yaml', 'csv'] as const)

    for (const lang of languagesToCheck) {
      const result = validationStrategies[lang].validate(content)
      if (result.isValid) {
        setDetectedLanguage(lang)
        if (editorRef.current) {
          monaco.editor.setModelLanguage(editorRef.current.getModel()!, lang)
        }
        onErrorClearRef.current()
        return lang // Return the detected language instead of calling validateContent
      }
    }

    // If we have required languages and none matched, show a specific error
    if (requireLangsRef.current.length > 0) {
      const requiredFormats = requireLangsRef.current.map(lang => lang.toUpperCase()).join(', ')
      setValidationError(`Invalid format. Content must be valid: ${requiredFormats}.`)
      onErrorRef.current()
    } else {
      setValidationError('Invalid format. Must be one of: XML, JSON, YAML, or CSV.')
      onErrorRef.current()
    }

    setDetectedLanguage('plaintext') // Default to plaintext if no valid format is detected
    if (editorRef.current) {
      monaco.editor.setModelLanguage(editorRef.current.getModel()!, 'plaintext')
    }
    return 'plaintext'
  }, [])

  const validateContent = useCallback(
    (content: string, language: string) => {
      if (language === 'auto') {
        const detectedLang = detectLanguage(content)
        // Now validate with the detected language
        if (detectedLang && detectedLang !== 'plaintext' && validationStrategies[detectedLang]) {
          const result = validationStrategies[detectedLang].validate(content)
          if (!result.isValid) {
            setValidationError(result.error || 'Unknown error')
            onErrorRef.current()
            markErrors(result)
          }
        }
      } else if (editorRef.current && validationStrategies[language]) {
        const result = validationStrategies[language].validate(content)
        if (result.isValid) {
          // If language is valid but requireLangs is specified, check if this language is allowed
          if (
            requireLangsRef.current.length > 0 &&
            !requireLangsRef.current.includes(language as Language)
          ) {
            const requiredFormats = requireLangsRef.current
              .map(lang => lang.toUpperCase())
              .join(', ')
            setValidationError(`Content format not allowed. Must be one of: ${requiredFormats}.`)
            onErrorRef.current()
          } else {
            setValidationError(null)
            errorDecorations.current = editorRef.current.deltaDecorations(
              errorDecorations.current,
              []
            )
            onErrorClearRef.current()
          }
        } else {
          setValidationError(result.error || 'Unknown error')
          onErrorRef.current()
          markErrors(result)
        }
      } else {
        // Check if the current language is allowed by requireLangs
        if (
          requireLangsRef.current.length > 0 &&
          !requireLangsRef.current.includes(language as Language)
        ) {
          const requiredFormats = requireLangsRef.current.map(lang => lang.toUpperCase()).join(', ')
          setValidationError(`Content format not allowed. Must be one of: ${requiredFormats}.`)
          onErrorRef.current()
        } else {
          setValidationError(null)
          onErrorClearRef.current()
          if (editorRef.current) {
            errorDecorations.current = editorRef.current.deltaDecorations(
              errorDecorations.current,
              []
            )
          }
        }
      }
    },
    [detectLanguage, markErrors]
  )

  // Update validateContentRef once
  validateContentRef.current = validateContent

  useEffect(() => {
    if (containerRef.current) {
      editorRef.current = monaco.editor.create(containerRef.current, {
        value: value,
        theme: theme,
        automaticLayout: true,
        minimap: { enabled: false },
        ...getMonacoOptions(),
        ...options,
        language: detectedLanguage,
        // Hide line numbers
        lineNumbers: 'off',
        // Hide folding controls
        folding: false,
        // Hide glyph margin
        glyphMargin: false,
        // Hide line decorations
        lineDecorationsWidth: 0,
        renderLineHighlight: 'none'
      })

      editorRef.current.onDidChangeModelContent(() => {
        const newValue = editorRef.current!.getValue()
        onChangeRef.current(newValue)
        const hasText = !!newValue.trim()
        setHasContent(hasText)
        if (validateContentRef.current) {
          validateContentRef.current(newValue, languageRef.current)
        }
      })

      setHasContent(!!value.trim())
      if (validateContentRef.current) {
        validateContentRef.current(value, languageRef.current)
      }
    }

    return () => {
      editorRef.current?.dispose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (editorRef.current) {
      const model = editorRef.current.getModel()
      if (model && value !== model.getValue()) {
        model.setValue(value)
        setHasContent(!!value.trim())
        if (validateContentRef.current) {
          validateContentRef.current(value, languageRef.current)
        }
      }
    }
  }, [value])

  useEffect(() => {
    languageRef.current = language
    if (editorRef.current) {
      if (language === 'auto') {
        detectLanguage(editorRef.current.getValue())
      } else {
        setDetectedLanguage(language)
        monaco.editor.setModelLanguage(editorRef.current.getModel()!, language)
        if (validateContentRef.current) {
          validateContentRef.current(editorRef.current.getValue(), language)
        }
      }
    }
  }, [language, detectLanguage])

  useEffect(() => {
    if (disabled) {
      editorRef.current?.updateOptions({ readOnly: true })
    } else {
      editorRef.current?.updateOptions({ readOnly: false })
    }
  }, [disabled])

  const [isFocused, setIsFocused] = useState(false)

  return (
    <div
      onFocus={() => {
        if (!disableFocusEffect) {
          setIsFocused(true)
        }
      }}
      onBlur={() => {
        if (!disableFocusEffect) {
          setIsFocused(false)
        }
      }}
      className={cm(
        'relative',
        'monaco-container w-full rounded-xl overflow-hidden p-2 bg-white',
        isFocused && !disabled ? 'border-[#AB97FE]' : 'border-[#EAEAEA]',
        disabled ? 'opacity-80' : '',
        className,
        noBorder ? 'border-none' : ''
      )}
      style={{ backgroundColor }}
    >
      <div
        ref={containerRef}
        className={cm('monaco-editor-wrapper h-[450px]', innerClassName)}
      ></div>
      {!hasContent && placeholder && (
        <div
          className={cm(
            'monaco-placeholder',
            'absolute whitespace-pre-wrap top-2 text-sm left-2 text-[#9da3ae] pointer-events-none select-none z-1'
          )}
        >
          {placeholder}
        </div>
      )}
      {validationError && <ErrorBlock>{validationError}</ErrorBlock>}
    </div>
  )
}
